import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Chiama callback() ogni volta che l'app/tab torna in foreground.
 *
 * - Mobile (Capacitor iOS/Android): usa App.addListener('appStateChange')
 * - Web: usa Page Visibility API (document.visibilitychange)
 *
 * IMPORTANTE: passare una callback stabile (useCallback con dipendenze corrette)
 * per evitare che l'effect si ri-monti ad ogni render.
 */
export function useAppFocus(callback: () => void): void {
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            let handle: { remove: () => Promise<void> } | null = null;
            // La registrazione del listener è asincrona: se l'effect viene ripulito
            // prima che la promise risolva, `handle` è ancora null e il listener
            // resterebbe agganciato per sempre. Con `callback` che cambia ad ogni
            // login/logout (dipende da session.user.id) i listener si accumulavano,
            // moltiplicando i refetch ad ogni ritorno in foreground.
            let cancelled = false;

            import('@capacitor/app').then(({ App }) => {
                return App.addListener('appStateChange', ({ isActive }) => {
                    if (isActive) callback();
                });
            }).then(h => {
                handle = h;
                if (cancelled) h.remove();
            }).catch(() => {
                // @capacitor/app non disponibile — silently ignore
            });

            return () => {
                cancelled = true;
                handle?.remove();
            };
        } else {
            const handler = () => {
                if (!document.hidden) callback();
            };
            document.addEventListener('visibilitychange', handler);
            return () => document.removeEventListener('visibilitychange', handler);
        }
    }, [callback]);
}
